import React, { useState } from 'react';
import { IconSuccess } from './icons/IconPack';

interface ContactProps {
  language: string;
}

const Contact: React.FC<ContactProps> = ({ language }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
        credentials: 'include'
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error('Error parsing response:', e);
        throw new Error(language === 'zh' ? '伺服器回應無效' : 'Invalid response from server');
      }
      
      if (!response.ok) {
        throw new Error(data.details || data.error || (language === 'zh' ? '發送訊息失敗' : 'Failed to send message'));
      }
      
      console.log('Success:', data);
      setFormData({
        name: '',
        email: '',
        service: '',
        message: ''
      });
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError(err.message || (language === 'zh' ? '發送訊息失敗，請稍後再試' : 'Failed to send message. Please try again later.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-heading-lg text-center mb-12 section-heading-underline centered">
          {language === 'zh' ? '聯絡我們' : 'Contact Our Team'}
        </h2>
        
        {!isSubmitted ? (
          <div className="max-w-lg mx-auto bg-white rounded-lg shadow-sm p-8 border border-gray-100">
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
                  {error}
                </div>
              )}
              
              <div className="mb-6">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">{language === 'zh' ? '姓名' : 'Full Name'}</label>
                <input 
                  id="name"
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={language === 'zh' ? '請輸入您的姓名' : 'Enter your full name'}
                  required
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">{language === 'zh' ? '電子郵件' : 'Email Address'}</label>
                <input 
                  id="email"
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={language === 'zh' ? '請輸入您的電子郵件' : 'Enter your email address'}
                  required
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              
              <div className="mb-6">
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">{language === 'zh' ? '服務項目' : 'Service of Interest'}</label>
                <select 
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="" disabled>{language === 'zh' ? '請選擇服務項目' : 'Select a service'}</option>
                  <option value="ai-development">{language === 'zh' ? 'AI 應用開發' : 'AI Development'}</option>
                  <option value="chatbot">{language === 'zh' ? '智能問答系統' : 'Chatbot Solutions'}</option>
                  <option value="customer-service">{language === 'zh' ? '多語言客服系統' : 'Multilingual Customer Service'}</option>
                  <option value="computer-vision">{language === 'zh' ? '電腦視覺系統' : 'Computer Vision Systems'}</option>
                  <option value="transcription">{language === 'zh' ? 'AI 智能客服中心' : 'Voice and Transcription Technology'}</option>
                  <option value="knowledge-base">{language === 'zh' ? 'AI 知識管理系統' : 'Ai-Powered Knowledge Management'}</option>
                  <option value="translation">{language === 'zh' ? '專業領域翻譯' : 'Domain Specific Translation'}</option>
                  <option value="training">{language === 'zh' ? 'AI 專業培訓' : 'Professional Training'}</option>
                  <option value="ai-infrastructure">{language === 'zh' ? 'AI 基礎設施建置' : 'AI Infrastructure'}</option>
                  <option value="consultant">{language === 'zh' ? 'AI 策略顧問' : 'Strategic Consultation'}</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">{language === 'zh' ? '訊息內容' : 'Your Message'}</label>
                <textarea 
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={language === 'zh' ? '請詳細說明您的需求或問題' : 'Describe your requirements or questions'}
                  required
                  rows={6}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                ></textarea>
              </div>
              
              <button 
                type="submit"
                className="button-primary w-full flex justify-center items-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (language === 'zh' ? '發送中...' : 'Sending...') : (language === 'zh' ? '送出查詢' : 'Submit Inquiry')}
              </button>
              
              <p className="text-caption text-gray-500 mt-4 text-center">
                {language === 'zh' ? '您的資訊將受到保護。訊息將直接發送至我們的團隊信箱：administrator@dhtsolution.com' : 'Your information is secure. Messages are sent directly to our team at administrator@dhtsolution.com'}
              </p>
            </form>
          </div>
        ) : (
          <div className="max-w-lg mx-auto text-center">
            <div className="bg-green-50 border border-green-100 text-green-800 p-8 rounded-lg mb-6 shadow-sm">
              <div className="icon-container primary mx-auto mb-4">
                <IconSuccess className="w-8 h-8" />
              </div>
              <h3 className="text-heading-md text-green-700 mb-3">
                {language === 'zh' ? '訊息已成功送出' : 'Message Sent Successfully'}
              </h3>
              <p className="text-body-md">
                {language === 'zh' 
                  ? '感謝您聯繫迪衡數位科技。我們的專業團隊將盡快處理您的需求並與您聯繫。'
                  : 'Thank you for reaching out to DHT Solution. Our team will review your inquiry and respond promptly.'}
              </p>
            </div>
            <button 
              onClick={() => setIsSubmitted(false)} 
              className="button-secondary"
            >
              {language === 'zh' ? '發送新訊息' : 'Send Another Message'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Contact;